import type { Dessert, DessertSet, Partner, PartnerBranch, SetItem } from "./types";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? "http://localhost:8000").replace(
  /\/$/,
  ""
);
const STORE_API_KEY = process.env.REACT_APP_STORE_API_KEY ?? "";

// The baldai.kz catalog site's endpoints live under their own router,
// separate from the staff app (see website_urls.py, mounted at
// baldai/website/ in config/api_router.py) — changes to the staff API can't
// accidentally break this client, and vice versa.
const WEBSITE_API_BASE = `${API_BASE_URL}/api/baldai/website`;

const GOODS_ENDPOINT = `${WEBSITE_API_BASE}/store/goods/`;
const CATEGORIES_ENDPOINT = `${WEBSITE_API_BASE}/store/good-categories/`;
const SETS_ENDPOINT = `${WEBSITE_API_BASE}/store/sets/`;
const CUSTOMERS_ENDPOINT = `${WEBSITE_API_BASE}/customers/`;
const SHARED_ORDER_ENDPOINT = `${WEBSITE_API_BASE}/orders/shared/`;

// Single fallback image used for any good/set/item that has no uploaded image.
// The backend serves real images (absolute URLs); this is only the template
// shown when `image` is null or empty.
const PLACEHOLDER_IMAGE =
  "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=600&q=80";

function imageOrPlaceholder(image: string | null | undefined): string {
  return image && image.trim() !== "" ? image : PLACEHOLDER_IMAGE;
}

// Backend BaldaiGoodSerializer shape. Money fields serialize as decimal strings;
// retail_price is nullable.
interface StoreGood {
  id: number;
  title: string;
  website_title: string;
  image: string | null;
  category: string | null;
  measure: string;
  description: string;
  website_description: string;
  retail_price: string | null;
  wholesale_price: string | null;
  is_b2b: boolean;
  is_b2c: boolean;
}

// Backend BaldaiGoodCategorySerializer shape.
interface StoreCategory {
  id: number;
  name: string;
  description: string;
}

// Backend BaldaiGoodSetItemSerializer shape. quantity is a Decimal, so DRF
// serializes it as a string ("1.00").
interface StoreGoodSetItem {
  good_id: number;
  good_title: string;
  good_description: string;
  good_image: string | null;
  quantity: string | number;
  sequence: number;
}

// Backend BaldaiCustomerBranchSerializer shape.
interface StoreCustomerBranch {
  website_title: string;
  address: string;
  latitude: string | number | null;
  longitude: string | number | null;
  location_code: string | null;
  link_2gis: string | null;
}

// Backend BaldaiCustomerGoodSerializer shape.
interface StoreCustomerGood {
  good_id: number;
  good_title: string;
}

// Backend BaldaiCustomerSerializer shape. The serializer omits a primary key,
// so the frontend assigns a synthetic id by list order.
interface StoreCustomer {
  name: string;
  category: string | null;
  branches: StoreCustomerBranch[];
  goods: StoreCustomerGood[];
}

// Backend BaldaiGoodSetSerializer shape.
interface StoreGoodSet {
  id: number;
  name: string;
  description: string;
  image: string | null;
  price: string | null;
  items: StoreGoodSetItem[];
}

// DRF pagination wraps the list in { count, results }.
interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function toNumber(value: string | number | null | undefined): number {
  if (value == null) {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

// Authenticated GET that walks DRF pagination `next` links so we always return
// every record, not just the first page.
async function fetchAll<T>(endpoint: string): Promise<T[]> {
  const items: T[] = [];
  let url: string | null = endpoint;

  while (url) {
    const response: Response = await fetch(url, {
      headers: {
        Accept: "application/json",
        Authorization: `ApiKey ${STORE_API_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load ${endpoint}: ${response.status} ${response.statusText}`);
    }

    const data: Paginated<T> | T[] = await response.json();
    if (Array.isArray(data)) {
      items.push(...data);
      url = null;
    } else {
      items.push(...data.results); 
      url = data.next;
    }
  }

  return items;
}

function mapGoodToDessert(good: StoreGood): Dessert {
  return {
    id: good.id,
    name: good.website_title?.trim() || good.title,
    category: good.category ?? "Other",
    price: toNumber(good.retail_price ?? good.wholesale_price),
    description: good.website_description?.trim() || good.description || "",
    image: imageOrPlaceholder(good.image)
  };
}

function mapSetItem(item: StoreGoodSetItem): SetItem {
  return {
    name: item.good_title,
    quantity: toNumber(item.quantity),
    unit: "pcs",
    image: imageOrPlaceholder(item.good_image),
    description: item.good_description ?? ""
  };
}

function mapSet(set: StoreGoodSet): DessertSet {
  return {
    id: set.id,
    name: set.name,
    tagline: "",
    description: set.description ?? "",
    mainImage: imageOrPlaceholder(set.image),
    price: toNumber(set.price),
    items: [...set.items].sort((a, b) => a.sequence - b.sequence).map(mapSetItem)
  };
}

export async function fetchGoods(): Promise<Dessert[]> {
  const goods = await fetchAll<StoreGood>(GOODS_ENDPOINT);
  return goods.map(mapGoodToDessert);
}

export async function fetchCategories(): Promise<string[]> {
  const categories = await fetchAll<StoreCategory>(CATEGORIES_ENDPOINT);
  return categories.map((category) => category.name);
}

export async function fetchSets(): Promise<DessertSet[]> {
  const sets = await fetchAll<StoreGoodSet>(SETS_ENDPOINT);
  return sets.map(mapSet);
}

// Parse a branch's lat/lng into a [longitude, latitude] tuple. Returns null
// when either value is missing, non-numeric, or the (0, 0) "unset" placeholder.
function parseBranchCoordinates(branch: StoreCustomerBranch): [number, number] | null {
  if (branch.latitude == null || branch.longitude == null) {
    return null;
  }
  const lat = Number(branch.latitude);
  const lng = Number(branch.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    return null;
  }
  return [lng, lat];
}

function mapCustomerBranch(branch: StoreCustomerBranch): PartnerBranch {
  return {
    // Display uses website_title, falling back to address when it's empty.
    title: branch.website_title?.trim() || branch.address?.trim() || "",
    address: branch.address?.trim() || "",
    coordinates: parseBranchCoordinates(branch),
    link2gis: branch.link_2gis ?? null
  };
}

function mapCustomerToPartner(customer: StoreCustomer, index: number): Partner {
  const branches = customer.branches.map(mapCustomerBranch);
  return {
    id: index + 1,
    name: customer.name,
    tagline: customer.category ?? "",
    locations: branches
      .map((branch) => branch.title)
      .filter((location) => location && location.trim() !== ""),
    branches,
    suppliedDessertIds: customer.goods.map((good) => good.good_id)
  };
}

export async function fetchPartners(): Promise<Partner[]> {
  const customers = await fetchAll<StoreCustomer>(CUSTOMERS_ENDPOINT);
  return customers.map(mapCustomerToPartner);
}

// Backend SharedOrderGoodSerializer shape. Money/decimal fields serialize as
// strings.
interface StoreSharedOrderGood {
  title: string;
  category: string | null;
  description: string;
  measure: string;
  image: string | null;
  quantity: string | number;
  unit_price: string | number;
  price: string | number;
}

// Backend SharedOrderSerializer shape (read-only, customer-facing).
interface StoreSharedOrder {
  share_token: string;
  date_requested: string | null;
  date_delivered: string | null;
  sequence_number: number | null;
  invoice_number: number | null;
  status: string;
  status_code: string;
  customer_name: string;
  branch_name: string;
  is_self_pickup: boolean;
  price: string | number;
  items: StoreSharedOrderGood[];
}

export interface SharedOrderItem {
  title: string;
  category: string;
  description: string;
  measure: string;
  image: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface SharedOrder {
  token: string;
  dateRequested: string | null;
  dateDelivered: string | null;
  sequenceNumber: number | null;
  invoiceNumber: number | null;
  status: string;
  statusCode: string;
  customerName: string;
  branchName: string;
  isSelfPickup: boolean;
  total: number;
  items: SharedOrderItem[];
}

// Thrown when the share token doesn't resolve to an order (404). Lets the page
// distinguish "no such order" from a transient network/server failure.
export class SharedOrderNotFoundError extends Error {}

function mapSharedOrderItem(item: StoreSharedOrderGood): SharedOrderItem {
  return {
    title: item.title,
    category: item.category ?? "",
    description: item.description ?? "",
    measure: item.measure,
    image: imageOrPlaceholder(item.image),
    quantity: toNumber(item.quantity),
    unitPrice: toNumber(item.unit_price),
    total: toNumber(item.price)
  };
}

export async function fetchSharedOrder(token: string): Promise<SharedOrder> {
  // Public endpoint: the share token is the only secret, so no ApiKey header.
  const response = await fetch(`${SHARED_ORDER_ENDPOINT}${encodeURIComponent(token)}/`, {
    headers: {
      Accept: "application/json"
    }
  });

  if (response.status === 404) {
    throw new SharedOrderNotFoundError("Order not found");
  }
  if (!response.ok) {
    throw new Error(`Failed to load order: ${response.status} ${response.statusText}`);
  }

  const data: StoreSharedOrder = await response.json();
  return {
    token: data.share_token,
    dateRequested: data.date_requested,
    dateDelivered: data.date_delivered,
    sequenceNumber: data.sequence_number,
    invoiceNumber: data.invoice_number,
    status: data.status ?? "",
    statusCode: data.status_code ?? "",
    customerName: data.customer_name ?? "",
    branchName: data.branch_name ?? "",
    isSelfPickup: Boolean(data.is_self_pickup),
    total: toNumber(data.price),
    items: (data.items ?? []).map(mapSharedOrderItem)
  };
}
